import { useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ChevronDown, Menu, X, Bot } from "lucide-react";
import { COMPANY, PRODUCTS, SERVICES } from "@/data/site";
import { SERVICES_MENU } from "@/data/servicesMenu";
import { readCache, fetchWithCache } from "@/lib/swrCache";

/** Fallback nav tree used ONLY when the /api/nav-items call has never run.
 * Shape matches what the CMS returns. Services are the Phase 3.1 canonical
 * set (10 pages) — grouped in code (see servicesMenu.js) but flattened here
 * so the existing HoverDropdown continues to render without refactor. */
const SERVICES_FALLBACK_CHILDREN = SERVICES_MENU.flatMap((g) =>
  g.items.map((it) => ({ label: it.label, path: it.path, tagline: it.tagline }))
);

const FALLBACK_NAV_TREE = [
  { id: "srv", label: "Services", path: "/services", children: SERVICES_FALLBACK_CHILDREN },
  { id: "prd", label: "Products", path: "/products", children: PRODUCTS.map((p) => ({ label: p.name, path: `/products/${p.slug}`, tagline: p.tagline })) },
  { id: "car", label: "Career",      path: "/careers" },
  { id: "abt", label: "About",       path: "/about" },
  { id: "con", label: "Contact",     path: "/contact" },
  { id: "par", label: "Partnership", path: "/partnership" },
];

// Reusable hover dropdown: opens on mouse-enter of trigger OR menu, closes on
// a brief 180 ms delay so users can slide between them without the menu
// disappearing. `items` is a list of {label, path, tagline?}.
function HoverDropdown({ to, label, items, testidRoot }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const closeTimer = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const openNow = () => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
    setOpen(true);
  };
  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 180);
  };

  return (
    <div
      ref={wrapRef}
      className="relative"
      onMouseEnter={openNow}
      onMouseLeave={scheduleClose}
      data-testid={`nav-${testidRoot}-dropdown`}
    >
      <NavLink
        to={to}
        onClick={() => setOpen(false)}
        data-testid={`nav-link-${testidRoot}`}
        className={({ isActive }) =>
          `px-4 py-2 text-sm rounded-full inline-flex items-center gap-1 transition-colors duration-200 ${
            isActive
              ? "text-primary bg-primary/10"
              : "text-foreground/80 hover:text-foreground hover:bg-foreground/5"
          }`
        }
      >
        {label} <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </NavLink>

      {open && (
        <div
          className="absolute left-1/2 -translate-x-1/2 top-full pt-3 z-50"
          onMouseEnter={openNow}
          onMouseLeave={scheduleClose}
        >
          <div
            className="w-80 rounded-2xl bg-popover text-popover-foreground shadow-[0_28px_60px_-20px_rgba(0,0,0,0.35)] ring-1 ring-border/70 p-2"
            data-testid={`nav-${testidRoot}-menu`}
          >
            {items.map((item, i) => {
              const key = item.id || item.path || `${item.label}-${i}`;
              const external = /^https?:\/\//i.test(item.path || "");
              const cls = "flex flex-col px-4 py-2.5 rounded-xl hover:bg-foreground/5 transition-colors";
              const testid = `nav-${testidRoot}-${(item.slug || item.label || "").toLowerCase().replace(/\s+/g, "-")}`;
              const inner = (
                <>
                  <span className="text-sm font-medium leading-tight">{item.label || item.name}</span>
                  {item.tagline && <span className="mt-0.5 text-[11px] text-muted-foreground line-clamp-1">{item.tagline}</span>}
                </>
              );
              return external ? (
                <a key={key} href={item.path} target="_blank" rel="noreferrer" onClick={() => setOpen(false)} className={cls} data-testid={testid}>{inner}</a>
              ) : (
                <Link key={key} to={item.path} onClick={() => setOpen(false)} className={cls} data-testid={testid}>{inner}</Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [mobileOpenId, setMobileOpenId] = useState(null); // which parent expanded on mobile
  const loc = useLocation();

  // CMS-managed nav — hydrate synchronously from sessionStorage.
  const [flatItems, setFlatItems] = useState(() => readCache("/nav-items")?.data || null);
  useEffect(() => {
    fetchWithCache("/nav-items", (fresh) => setFlatItems(fresh || [])).catch(() => {});
  }, []);

  // Build a top-level tree from the flat list. If the API returned nothing yet,
  // fall back to the hard-coded tree so the header is never empty on first paint.
  const navTree = useMemo(() => {
    if (!flatItems || flatItems.length === 0) return FALLBACK_NAV_TREE;
    const parents = flatItems.filter((n) => !n.parent_id).sort((a, b) => (a.order || 0) - (b.order || 0));
    const childrenByParent = flatItems.reduce((acc, n) => {
      if (!n.parent_id) return acc;
      (acc[n.parent_id] ||= []).push(n);
      return acc;
    }, {});
    return parents.map((p) => ({
      ...p,
      children: (childrenByParent[p.id] || []).sort((a, b) => (a.order || 0) - (b.order || 0)),
    }));
  }, [flatItems]);

  const isExternal = (p) => /^https?:\/\//i.test(p || "");
  const testidFor = (label) => (label || "").toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");

  return (
    <header className="fixed top-11 left-4 right-4 z-50" data-testid="site-navbar">
      <nav className="glass rounded-full pl-6 pr-3 py-3 flex items-center justify-between shadow-2xl max-w-[1600px] mx-auto">
        <Link to="/" className="flex items-center gap-2 group" data-testid="nav-logo">
          <img
            src={COMPANY.logo}
            alt="CloudLab Technologies"
            className="h-8 w-auto transition-transform duration-300 group-hover:scale-105"
          />
          <span className="font-heading font-bold tracking-tight text-base md:text-lg hidden sm:inline whitespace-nowrap">Cloud Lab Technologies</span>
        </Link>

        <div className="hidden lg:flex items-center gap-1">
          {navTree.map((n) => {
            const testid = testidFor(n.label);
            if (n.children && n.children.length > 0) {
              return (
                <HoverDropdown
                  key={n.id || n.path}
                  to={n.path}
                  label={n.label}
                  items={n.children}
                  testidRoot={testid}
                />
              );
            }
            const cls = ({ isActive }) =>
              `px-4 py-2 text-sm rounded-full transition-colors duration-200 ${
                isActive ? "text-primary bg-primary/10" : "text-foreground/80 hover:text-foreground hover:bg-foreground/5"
              }`;
            if (isExternal(n.path)) {
              return (
                <a
                  key={n.id || n.path}
                  href={n.path}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`nav-link-${testid}`}
                  className="px-4 py-2 text-sm rounded-full transition-colors duration-200 text-foreground/80 hover:text-foreground hover:bg-foreground/5"
                >
                  {n.label}
                </a>
              );
            }
            return (
              <NavLink key={n.id || n.path} to={n.path} data-testid={`nav-link-${testid}`} className={cls}>
                {n.label}
              </NavLink>
            );
          })}
        </div>

        <div className="flex items-center gap-2">
          <button
            data-testid="nav-cta-ai-agent"
            onClick={() => window.dispatchEvent(new CustomEvent("open-ai-agent"))}
            className="hidden md:inline-flex items-center gap-2 rounded-full px-4 py-2 bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity"
          >
            <span className="w-6 h-6 rounded-full bg-primary-foreground/20 flex items-center justify-center">
              <Bot className="w-3.5 h-3.5" />
            </span>
            Talk to AI Agent
          </button>
          <button
            className="lg:hidden p-2 rounded-full hover:bg-foreground/10"
            onClick={() => setOpen((o) => !o)}
            aria-label="Menu"
            data-testid="nav-mobile-toggle"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </nav>

      {open && (
        <div className="lg:hidden mt-2 glass rounded-3xl p-4 space-y-1" data-testid="mobile-nav-menu">
          {navTree.map((n) => {
            const testid = testidFor(n.label);
            if (n.children && n.children.length > 0) {
              const expanded = mobileOpenId === n.id;
              return (
                <div key={n.id || n.path}>
                  <button
                    type="button"
                    onClick={() => setMobileOpenId(expanded ? null : n.id)}
                    className="w-full flex items-center justify-between px-4 py-3 rounded-2xl text-sm hover:bg-foreground/5"
                    data-testid={`mobile-nav-${testid}`}
                  >
                    <span>{n.label}</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${expanded ? "rotate-180" : ""}`} />
                  </button>
                  {expanded && (
                    <div className="pl-3 pb-2 space-y-1">
                      <Link to={n.path} onClick={() => setOpen(false)} className="block px-4 py-2 rounded-xl text-sm hover:bg-foreground/5">
                        All {n.label.toLowerCase()}
                      </Link>
                      {n.children.map((c) => {
                        const cTestid = `mobile-nav-${testid}-${testidFor(c.label)}`;
                        return isExternal(c.path) ? (
                          <a key={c.id || c.path} href={c.path} target="_blank" rel="noreferrer" onClick={() => setOpen(false)}
                             className="block px-4 py-2 rounded-xl text-sm hover:bg-foreground/5" data-testid={cTestid}>
                            {c.label}
                          </a>
                        ) : (
                          <Link key={c.id || c.path} to={c.path} onClick={() => setOpen(false)}
                                className="block px-4 py-2 rounded-xl text-sm hover:bg-foreground/5" data-testid={cTestid}>
                            {c.label}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }
            const cls = `block px-4 py-3 rounded-2xl text-sm ${
              loc.pathname === n.path ? "bg-primary/10 text-primary" : "hover:bg-foreground/5"
            }`;
            return isExternal(n.path) ? (
              <a key={n.id || n.path} href={n.path} target="_blank" rel="noreferrer" onClick={() => setOpen(false)} className={cls} data-testid={`mobile-nav-${testid}`}>
                {n.label}
              </a>
            ) : (
              <Link key={n.id || n.path} to={n.path} onClick={() => setOpen(false)} className={cls} data-testid={`mobile-nav-${testid}`}>
                {n.label}
              </Link>
            );
          })}
          <button
            data-testid="mobile-nav-cta-ai-agent"
            onClick={() => { setOpen(false); window.dispatchEvent(new CustomEvent("open-ai-agent")); }}
            className="w-full inline-flex items-center justify-center gap-2 rounded-full mt-2 py-3 bg-primary text-primary-foreground text-sm font-medium"
          >
            <Bot className="w-4 h-4" /> Talk to AI Agent
          </button>
        </div>
      )}
    </header>
  );
}
